/* eslint-disable no-unused-expressions */
/* eslint-disable react/jsx-one-expression-per-line, no-restricted-syntax, no-return-assign, no-nested-ternary, no-param-reassign, no-confusing-arrow */
import React, { useState, useEffect, useMemo } from 'react';

// Redux
import { useSelector } from 'react-redux';

// Material
import TableCell from '@material-ui/core/TableCell';
import Box from '@material-ui/core/Box';
import Tr from '@material-ui/core/TableRow';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';

// Material Icons
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import InfoSharpIcon from '@material-ui/icons/InfoSharp';

// Date FNS
import format from 'date-fns/format';

// Lodash
import { groupBy } from 'lodash';

// Helpers
import { currencyFormat } from 'helpers/currencyFormat';
import { difference } from 'helpers/diffTimes';
import useWindowDimensions from 'helpers/useWindowDimensions';

// Types
import { TripReportInterface } from 'store/ducks/trips/types';
import { ApplicationState } from 'store';

// Constants
import { CONST_LINES_OBJECT } from 'constants/lines';

// Components
import { PrettiNameLine } from 'components/ReportComponents';

// Styles
import useStyles from './tableRow.styles';

interface SubRowInterface {
  toggleInfoDrawer: (open: boolean, data?: any) => (event: any) => void;
  collapseTrips: TripReportInterface[];
  index: number;
}

type Props = SubRowInterface;

const DRAWER_WIDTH = 300;
const BORDER_AND_SPACINGS = 100;

const SubRow: React.FC<Props> = ({
  toggleInfoDrawer,
  collapseTrips,
  index,
}) => {
  const classes = useStyles();

  const classRow = index % 2 === 0 ? classes.evenRow : classes.oddRow;

  const { width: windowWidth } = useWindowDimensions();

  const [benefitsList, setBenefitsList] = useState<string[]>([]);
  const [collapseSubRow, setCollapseSubRow] = useState(false);

  const kmType = useSelector((state: ApplicationState) => state.trips.kmType);
  const drawerOpen = useSelector(
    (state: ApplicationState) => state.drawer.open,
  );

  const totalKm = useMemo(
    () => (km: number, idleKm: number) => (km + idleKm).toFixed(2),
    [],
  );

  const renderBenefitValue = useMemo(
    () => (trip: TripReportInterface, benefit: string) => {
      if (trip.EntriesResume?.entries?.benefits) {
        const { benefits } = trip.EntriesResume.entries;
        return benefits.hasOwnProperty(benefit)
          ? // @ts-ignore
            Number(benefits[benefit].quantity)
            ? // @ts-ignore
              benefits[benefit].quantity
            : 0
          : // @ts-ignore
            (benefits[benefit] = 0);
      }
      return 0;
    },
    [],
  );
  const getFirstTrip = useMemo(() => {
    const found = collapseTrips.find((t) => !t.parentId);
    if (found) {
      return found;
    } else {
      return collapseTrips[0];
    }
  }, [collapseTrips]);

  const getHighestClosedAt = useMemo(() => {
    let highest = collapseTrips[0].closedAt;

    collapseTrips.forEach((item) => {
      if (highest < item.closedAt) highest = item.closedAt;
    });
    return highest;
  }, [collapseTrips]);

  const getTotals = useMemo(() => {
    const arrSize = collapseTrips.length;

    const kmTotal = collapseTrips
      .map((item) =>
        kmType === 'total'
          ? Number(totalKm(item.km, item.idleKm))
          : Number(item.km?.toFixed(2)),
      )
      .reduce((acc, curr) => acc + curr, 0);

    let kmDiscriminated = 0;
    if (kmType === 'discriminated') {
      kmDiscriminated = collapseTrips
        .map((item) => Number(item.idleKm?.toFixed(2)))
        .reduce((acc, curr) => acc + curr, 0);
    }

    const valuePerKm =
      collapseTrips
        .map((item) => item.valuePerKm || 0)
        .reduce((acc, curr) => acc + Number(curr), 0) / arrSize;

    const ipkTotal =
      collapseTrips
        .map((item) => Number(item.ipk?.toFixed(2) || '0'))
        .reduce((acc, curr) => acc + curr, 0) / arrSize;

    const ipkeTotal =
      collapseTrips
        .map((item) => Number(item.ipke?.toFixed(2) || '0'))
        .reduce((acc, curr) => acc + curr, 0) / arrSize;

    const entriesResumeQuantity = collapseTrips
      .map((item) => Number(item?.EntriesResume?.quantity || '0'))
      .reduce((acc, curr) => acc + curr, 0);

    const totalPassengers = collapseTrips
      .map((item) => Number(item?.totalPassengers || '0'))
      .reduce((acc, curr) => acc + curr, 0);

    const totalValueTransported = collapseTrips
      .map((item: any) => {
        const resume = item?.resume ? JSON.parse(item?.resume) : null;

        if (resume) {
          return resume?.totalValue;
        }

        return 0;
      })
      .reduce((acc, curr) => acc + curr, 0);

    const benefitsListTotal = benefitsList.map((benefit) => {
      const result = collapseTrips
        .map((trip) => {
          return renderBenefitValue(trip, benefit);
        })
        .reduce((acc, curr) => acc + curr, 0);
      return result;
    });

    return {
      kmTotal,
      kmDiscriminated,
      valuePerKm: currencyFormat({ value: valuePerKm }),
      ipkTotal,
      ipkeTotal,
      entriesResumeQuantity,
      benefitsListTotal,
      totalPassengers,
      totalValueTransported,
    };
  }, [collapseTrips, benefitsList]);

  useEffect(() => {
    setBenefitsList(() => {
      const benefitsList: string[] = [];
      if (Array.isArray(collapseTrips) && collapseTrips.length > 0) {
        for (const trip of collapseTrips) {
          const benefits = trip.EntriesResume?.entries?.benefits;
          if (benefits) {
            for (const benefit of Object.keys(benefits)) {
              if (!benefitsList.includes(benefit)) {
                benefitsList.push(benefit);
              }
            }
          }
        }
      }
      return benefitsList;
    });
  }, [collapseTrips]);

  return (
    <>
      <Tr className={classRow}>
        <TableCell />
        <TableCell>
          <Box display='flex' alignItems='center'>
            <Box display='flex' alignItems='center' pr={1} ml={-2}>
              <IconButton
                aria-label='expand row'
                size='small'
                onClick={() => setCollapseSubRow((o) => !o)}>
                {collapseSubRow ? (
                  <KeyboardArrowUpIcon />
                ) : (
                  <KeyboardArrowDownIcon />
                )}
              </IconButton>
            </Box>{' '}
            <PrettiNameLine
              line={`${getFirstTrip.LineSection?.Line?.prefix} - ${getFirstTrip.LineSection?.Line?.name}`}
              color={getFirstTrip.LineSection?.Line?.color || '#9427EA'}
            />
          </Box>
        </TableCell>
        <TableCell align='center'>--</TableCell>
        <TableCell align='center'>
          {getFirstTrip?.id?.slice(0, 8) || '--'}
        </TableCell>
        <TableCell align='center'>
          {getFirstTrip?.Car?.prefix || '--'}
        </TableCell>
        <TableCell align='center'>
          {getFirstTrip.startedAt
            ? format(new Date(getFirstTrip.startedAt), 'dd/MM/yyyy')
            : '--'}
        </TableCell>
        <TableCell align='center'>
          {getFirstTrip.startedAt
            ? format(new Date(getFirstTrip.startedAt), 'HH:mm:ss')
            : '--'}
        </TableCell>
        <TableCell align='center'>
          {getHighestClosedAt
            ? format(
                new Date(getHighestClosedAt),
                getFirstTrip.startedAt &&
                  format(new Date(getFirstTrip.startedAt), 'dd/MM/yyyy') !==
                    format(new Date(getHighestClosedAt), 'dd/MM/yyyy')
                  ? 'dd/MM/yyyy HH:mm:ss'
                  : 'HH:mm:ss',
              )
            : '--'}
        </TableCell>
        <TableCell align='center'>
          {getFirstTrip?.closedAt && getFirstTrip.startedAt
            ? difference(getHighestClosedAt, getFirstTrip.startedAt)
            : '--'}
        </TableCell>
        <TableCell align='center'>{getTotals.kmTotal.toFixed(2)}</TableCell>
        {kmType === 'discriminated' && (
          <TableCell>{getTotals.kmDiscriminated}</TableCell>
        )}
        <TableCell align='center'>{getTotals.valuePerKm}</TableCell>
        <TableCell align='center'>{getTotals.ipkTotal.toFixed(2)}</TableCell>
        <TableCell align='center'>{getTotals.ipkeTotal.toFixed(2)}</TableCell>
        <TableCell align='center'>{getTotals.entriesResumeQuantity}</TableCell>
        <TableCell align='center'>{getTotals.totalPassengers}</TableCell>
        <TableCell align='center'>
          {currencyFormat({ value: getTotals.totalValueTransported || 0 })}
        </TableCell>
        {Array.isArray(benefitsList) &&
          getTotals.benefitsListTotal.map((benefitTotal) => (
            <TableCell align='center' key={benefitTotal}>
              {benefitTotal}
            </TableCell>
          ))}
        <TableCell align='right'>
          <Box visibility='hidden'>
            <Tooltip placement='top' title='Informações da viagem'>
              <IconButton>
                <InfoSharpIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </Tr>
      {collapseSubRow &&
        collapseTrips.length > 0 &&
        Object.entries(
          groupBy(
            collapseTrips.sort((a: any, b: any) => {
              if (a?.LineSection && b?.LineSection) {
                return a?.LineSection?.Line?.name.localeCompare(
                  b?.LineSection?.Line?.name,
                );
              }
            }),
            (item) => item.LineSection?.Line?.name,
          ),
        ).map(([key, trip]: any) =>
          trip.map((item: any) => {
            const resume = item?.resume ? JSON.parse(item?.resume) : null;

            return (
              <Tr key={item.id} className={classRow}>
                <TableCell />
                <TableCell>
                  <Box display='flex' alignItems='center'>
                    <Box width='30px' pr={1} />
                    <PrettiNameLine
                      line={`${trip[0].LineSection?.Line?.prefix} - ${key}`}
                      color={trip[0].LineSection?.Line?.color || '#9427EA'}
                    />
                  </Box>
                </TableCell>
                <TableCell align='center'>
                  {item?.LineSection?.way === 0
                    ? CONST_LINES_OBJECT.WAYS.GO.VALUE
                    : CONST_LINES_OBJECT.WAYS.BACK.VALUE}
                </TableCell>
                <TableCell align='center'>
                  {item?.id?.slice(0, 8) || '--'}
                </TableCell>
                <TableCell align='center'>
                  {item?.Car?.prefix || '--'}
                </TableCell>
                <TableCell align='center'>
                  {item.startedAt
                    ? format(new Date(item.startedAt), 'dd/MM/yyyy')
                    : '--'}
                </TableCell>
                <TableCell align='center'>
                  {item.startedAt
                    ? format(new Date(item.startedAt), 'HH:mm:ss')
                    : '--'}
                </TableCell>
                <TableCell align='center'>
                  {item?.closedAt
                    ? format(
                        new Date(item.closedAt),
                        item.startedAt &&
                          format(new Date(item.startedAt), 'dd/MM/yyyy') !==
                            format(new Date(item.closedAt), 'dd/MM/yyyy')
                          ? 'dd/MM/yyyy HH:mm:ss'
                          : 'HH:mm:ss',
                      )
                    : '--'}
                </TableCell>
                <TableCell align='center'>
                  {item?.closedAt && item.startedAt
                    ? difference(item.closedAt, item.startedAt)
                    : '--'}
                </TableCell>
                <TableCell align='center'>
                  {kmType === 'total'
                    ? totalKm(item.km, item.idleKm)
                    : item.km?.toFixed(2)}
                </TableCell>
                {kmType === 'discriminated' && (
                  <TableCell align='center'>
                    {item.idleKm?.toFixed(2)}
                  </TableCell>
                )}
                <TableCell align='center'>
                  {currencyFormat({ value: item.valuePerKm || 0 })}
                </TableCell>
                <TableCell align='center'>
                  {item.ipk?.toFixed(2) || '0.00'}
                </TableCell>
                <TableCell align='center'>
                  {item.ipke?.toFixed(2) || '0.00'}
                </TableCell>
                <TableCell align='center'>
                  {item?.EntriesResume?.quantity || '0'}
                </TableCell>
                <TableCell align='center'>
                  {item?.totalPassengers || '0'}
                </TableCell>
                <TableCell align='center'>
                  {resume
                    ? currencyFormat({ value: resume?.totalValue || 0 })
                    : '0'}
                </TableCell>
                {Array.isArray(benefitsList) &&
                  benefitsList.map((benefit) => (
                    <TableCell align='center' key={benefit}>
                      {renderBenefitValue(item, benefit)}
                    </TableCell>
                  ))}
                <TableCell align='right'>
                  <Tooltip placement='top' title='Informações da viagem'>
                    <IconButton onClick={toggleInfoDrawer(true, item)}>
                      <InfoSharpIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell />
              </Tr>
            );
          }),
        )}
    </>
  );
};

export { SubRow };
export default SubRow;
